import { useState, useEffect } from "react";
import {
  getShipmentSummaries,
  getSCIPLatestForecast,
  getSCIPForecasts,
} from "../graphql/queries";
import { getPaginatedAPICall } from "../utilities/get-paginated-api-call";
import { getStartOfDay } from "../utilities/get-start-of-day";
import { convertUnix } from "../utilities/convert-unix";
import { filterDisplayAppointments } from "../components/Constants";

export const useAnalyticsData = (warehouseId, open, timeZone) => {
  const [shipmentData, setShipmentData] = useState();
  const [shipments1day, setShipments1day] = useState([]);
  const [shipments2day, setShipments2day] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getAnalyticsData = async () => {
      if (!open) return;

      setIsLoading(true);
      const params = {
        warehouse: {
          warehouseId: warehouseId,
          minAppointmentTime: getStartOfDay(timeZone, 0),
          maxAppointmentTime: getStartOfDay(timeZone, 2),
        },
      };

      // Get shipment summaries
      const data = await getPaginatedAPICall(
        params,
        getShipmentSummaries,
        "getShipmentSummaries"
      );

      // Get latest snapshot time for SCIP
      const latestParams = { nothing: {} };
      const latestSnapshotData = await getPaginatedAPICall(
        latestParams,
        getSCIPLatestForecast,
        "getSCIPLatestForecast"
      );
      const latestSnapshotTime = latestSnapshotData
        ? latestSnapshotData[0].snapshot_time
        : 0;

      // Get SCIP forecasts
      const scipParams = {
        warehouse: {
          warehouseId: warehouseId,
          minAppointmentTime: getStartOfDay(timeZone, 0),
          maxAppointmentTime: getStartOfDay(timeZone, 2),
          latestSnapShotTime: latestSnapshotTime,
        },
      };
      const scipData = await getPaginatedAPICall(
        scipParams,
        getSCIPForecasts,
        "getSCIPForecasts"
      );

      // Format and combine data
      if (data && scipData) {
        // Process manifested shipments first
        const manifestedIncomingTSIs = data
          .filter(
            (shipment) =>
              shipment.orderId.startsWith("Trans_") && shipment.poItemSummary
          )
          .flatMap((shipment) => {
            const poSummary = JSON.parse(shipment.poItemSummary);
            return [
              {
                vehicleRunId: shipment.vehicleRunId,
                tempZone: Object.keys(poSummary.summaryByTempZone || {})[0],
                confirmedUnits:
                  Object.values(poSummary.summaryByTempZone || {})[0]
                    ?.manifestQuantity || 0,
              },
            ];
          });

        const dockReceivedTSIs = data
          .filter(
            (shipment) =>
              shipment.orderId.startsWith("Trans_") &&
              shipment.firstDockReceiveTime &&
              shipment.poItemSummary
          )
          .flatMap((shipment) => {
            const poSummary = JSON.parse(shipment.poItemSummary);
            return [
              {
                vehicleRunId: shipment.vehicleRunId,
                tempZone: Object.keys(poSummary.summaryByTempZone || {})[0],
                confirmedUnits:
                  Object.values(poSummary.summaryByTempZone || {})[0]
                    ?.wmsQuantityReceived || 0,
              },
            ];
          });

        const filteredScipData = scipData
          .filter((forecast) => forecast.source_ind === "DOCalculationService")
          .flatMap((forecast) => {
            // Get all VRIDs from load_id
            const allVrids = [
              ...new Set(forecast.load_id.replaceAll(" ", "").split(",")),
            ];
            if (allVrids.length === 0) return [];

            // Filter out VRIDs that are already manifested
            const remainingVrids = allVrids.filter(
              (vrid) =>
                manifestedIncomingTSIs.filter(
                  (tsi) => tsi.vehicleRunId === vrid
                ).length === 0 &&
                dockReceivedTSIs.filter((tsi) => tsi.vehicleRunId === vrid)
                  .length === 0
            );

            // Skip if all VRIDs are manifested
            if (remainingVrids.length <= 0) return [];

            const tempZone = forecast.temp_zone;

            // Calculate remaining quantity
            const manifestedIncomingQty = manifestedIncomingTSIs
              .filter(
                (tsi) =>
                  allVrids.includes(tsi.vehicleRunId) &&
                  tsi.tempZone === tempZone
              )
              .reduce((sum, tsi) => sum + tsi.confirmedUnits, 0);

            const dockReceivedQty = dockReceivedTSIs
              .filter(
                (tsi) =>
                  allVrids.includes(tsi.vehicleRunId) &&
                  tsi.tempZone === tempZone
              )
              .reduce((sum, tsi) => sum + tsi.confirmedUnits, 0);

            const totalManifestQty = manifestedIncomingQty + dockReceivedQty;
            const expectedQty = Math.max(
              forecast.expected_qty - totalManifestQty,
              0
            );

            // Skip if no remaining quantity
            if (expectedQty <= 0) return [];

            return [
              {
                tempZone: tempZone,
                loadType: forecast.ib_flow === "TSI" ? "Transship" : "Vendor",
                loadId: "Not Yet Manifested",
                source: forecast.origin_fc,
                expectedUnits: expectedQty,
                expectedUnitSource: `${
                  forecast.source_ind
                } Crossdock: ${forecast.expected_qty_xd.toLocaleString()}, DO Calc: ${forecast.expected_qty_doc.toLocaleString()}, MTR: ${forecast.expected_qty_mtr.toLocaleString()}, Rolled Freight: ${forecast.expected_qty_rf.toLocaleString()}`,
                scheduledArrivals: [forecast.expected_arrival_time],
                firstAppointmentSource: "ATS",
                stopSequence: forecast.stop_sequence,
                scheduledArrivalsParsed: [
                  convertUnix(
                    forecast.expected_arrival_time,
                    timeZone,
                    "sv-SE"
                  ),
                ],
                expectedArrivalTime: [undefined],
                expectedArrivalTimeSource: [undefined],
                expectedArrivalTimeParsed: [undefined],
                arrivalDatetime: [undefined],
                arrivalDatetimeSource: [undefined],
                arrivalDatetimeParsed: [undefined],
                shipmentId: remainingVrids,
                scac: "",
                ocrVendor: "N",
                timeZone: timeZone,
                transferId: forecast.transferId,
                logicSource: "TSI Forecasts",
              },
            ];
          });

        setShipmentData([...data, ...filteredScipData]);

        const todayStart = getStartOfDay(timeZone, 0);
        const tomorrowStart = getStartOfDay(timeZone, 1);
        const dayAfterTomorrowStart = getStartOfDay(timeZone, 2);

        const todayScip = filteredScipData.filter(
          (forecast) =>
            parseInt(forecast.scheduledArrivals[0]) >= todayStart &&
            parseInt(forecast.scheduledArrivals[0]) < tomorrowStart
        );
        const tomorrowScip = filteredScipData.filter(
          (forecast) =>
            parseInt(forecast.scheduledArrivals[0]) >= tomorrowStart &&
            parseInt(forecast.scheduledArrivals[0]) < dayAfterTomorrowStart
        );

        setShipments1day([...todayScip]);
        setShipments2day([...tomorrowScip]);
      } else {
        setShipmentData(data);
      }

      const todayStart = getStartOfDay(timeZone, 0);
      const tomorrowStart = getStartOfDay(timeZone, 1);
      const dayAfterTomorrowStart = getStartOfDay(timeZone, 2);

      const appointmentShipments = data.filter(
        (shipment) =>
          shipment.firstAppointmentSource === "APPOINTMENT" &&
          !shipment.firstDockReceiveTime
      );

      const processShipmentsByTempZone = (shipments) => {
        return shipments.flatMap((shipment) => {
          if (!shipment.poItemSummary) return [];

          const poSummary = JSON.parse(shipment.poItemSummary);
          const isTransship = shipment.orderId.startsWith("Trans_");

          // Process associatedAppointments for scheduledArrivals
          const associatedAppointments = shipment.associatedAppointments
            ? Object.entries(JSON.parse(shipment.associatedAppointments)).map(
                ([appointmentDetails]) => appointmentDetails
              )
            : [];

          // Get scheduledArrivals
          let scheduledArrivals = [];
          if (associatedAppointments && associatedAppointments.length > 0) {
            scheduledArrivals = filterDisplayAppointments(associatedAppointments)
              .map((appointment) => appointment.scheduledArrivalTime)
              .filter((time) => time != null);
          }
          // Default to firstAppointmentTime if no valid appointments and it exists
          if (scheduledArrivals.length === 0 && shipment.firstAppointmentTime) {
            scheduledArrivals = [shipment.firstAppointmentTime];
          }

          const loadType = isTransship ? "Transship" : "Vendor";
          const source = isTransship
            ? shipment.srcWarehouseId
            : shipment.distributorId;
          const loadId = shipment.orderId.substring(
            shipment.orderId.indexOf("_") + 1
          );

          if (isTransship) {
            const tempZones = poSummary.summaryByTempZone || {};
            return Object.entries(tempZones).map(([tempZone, quantities]) => {
              const asrsConfirmedUnits = quantities.asrsMainifestQuantity || 0;
              const confirmedUnits =
                quantities.manifestQuantity ||
                quantities.wmsQuantityReceived ||
                0;
              // Extract child quantities for parent-child conversion
              const childQuantity = quantities.childManifestQuantity || 
                                   quantities.childWmsQuantityReceived ||
                                   quantities.childQuantityConfirmed ||
                                   confirmedUnits;
              return {
                ...shipment,
                loadType,
                source,
                loadId,
                tempZone,
                expectedUnits: confirmedUnits,
                childQuantity: childQuantity,
                scheduledArrivals,
                scheduledArrivalsParsed: scheduledArrivals.map((time) =>
                  convertUnix(time, shipment.timeZone, "sv-SE")
                ),
                arrivalDatetime: [undefined],
                arrivalDatetimeParsed: [undefined],
                arrivalDatetimeSource: [undefined],
                expectedArrivalTime: [
                  shipment.vrEstimatedTimeOfArrival ||
                    shipment.vrPlannedCheckinTime ||
                    undefined,
                ],
                expectedArrivalTimeSource: [
                  shipment.vrEstimatedTimeOfArrival
                    ? "ETAPrediktion"
                    : shipment.vrPlannedCheckinTime
                    ? "SHREK"
                    : undefined,
                ],
                expectedArrivalTimeParsed: [
                  shipment.vrEstimatedTimeOfArrival ||
                  shipment.vrPlannedCheckinTime
                    ? convertUnix(
                        shipment.vrEstimatedTimeOfArrival ||
                          shipment.vrPlannedCheckinTime,
                        shipment.timeZone,
                        "sv-SE"
                      )
                    : undefined,
                ],
                asrsConfirmedUnits,
                confirmedUnits,
                expectedAsrsUnits: Math.round(
                  (asrsConfirmedUnits / confirmedUnits) * confirmedUnits
                ),
                expectedAsrsUnitsPercent:
                  Math.round(
                    (asrsConfirmedUnits / confirmedUnits) * 100 * 100
                  ) / 100,
                expectedUnitSource: "Manifest",
                shipmentId: [shipment.vehicleRunId],
                scac: associatedAppointments.map(
                  (appointment) => appointment.carrierId
                ),
                status: [shipment.freightArrivalStatus],
                logicSource: "Incoming",
              };
            });
          } else {
            const tempZones = poSummary.summaryByTempZoneDetailed || {};
            return Object.entries(tempZones).map(([tempZone, quantities]) => {
              const asrsConfirmedUnits = quantities.asrsQuantityConfirmed || 0;
              const confirmedUnits =
                quantities.quantityConfirmed ||
                quantities.quantitySubmitted ||
                0;
              // Extract child quantities for parent-child conversion
              const childQuantity = quantities.childQuantityConfirmed || 
                                   quantities.childQuantitySubmitted ||
                                   confirmedUnits;
              return {
                ...shipment,
                loadType,
                source,
                loadId,
                tempZone,
                expectedUnits: confirmedUnits,
                childQuantity: childQuantity,
                scheduledArrivals,
                scheduledArrivalsParsed: scheduledArrivals.map((time) =>
                  convertUnix(time, shipment.timeZone, "sv-SE")
                ),
                arrivalDatetime: [
                  shipment.freightActualArrivalTime || undefined,
                ],
                arrivalDatetimeParsed: [
                  shipment.freightActualArrivalTime
                    ? convertUnix(
                        shipment.freightActualArrivalTime,
                        shipment.timeZone,
                        "sv-SE"
                      )
                    : undefined,
                ],
                arrivalDatetimeSource: [
                  shipment.freightActualArrivalTime ? "10-4" : undefined,
                ],
                expectedArrivalTime: [
                  shipment.freightEstimatedArrivalTime || undefined,
                ],
                expectedArrivalTimeSource: [
                  shipment.freightEstimatedArrivalTime ? "10-4" : undefined,
                ],
                expectedArrivalTimeParsed: [
                  shipment.freightEstimatedArrivalTime
                    ? convertUnix(
                        shipment.freightEstimatedArrivalTime,
                        shipment.timeZone,
                        "sv-SE"
                      )
                    : undefined,
                ],
                asrsConfirmedUnits,
                confirmedUnits,
                expectedAsrsUnits: Math.round(
                  (asrsConfirmedUnits / confirmedUnits) * confirmedUnits
                ),
                expectedAsrsUnitsPercent:
                  Math.round(
                    (asrsConfirmedUnits / confirmedUnits) * 100 * 100
                  ) / 100,
                expectedUnitSource:
                  shipment.orderCondition === "CompletelyConfirmed"
                    ? "PO Confirmed"
                    : "PO Submitted",
                shipmentId: [],
                scac: associatedAppointments.map(
                  (appointment) => appointment.carrierId
                ),
                status: [shipment.freightArrivalStatus],
                logicSource: "Incoming",
              };
            });
          }
        });
      };

      const today = processShipmentsByTempZone(
        appointmentShipments.filter(
          (shipment) =>
            shipment.firstAppointmentTime >= todayStart &&
            shipment.firstAppointmentTime < tomorrowStart
        )
      );

      const tomorrow = processShipmentsByTempZone(
        appointmentShipments.filter(
          (shipment) =>
            shipment.firstAppointmentTime >= tomorrowStart &&
            shipment.firstAppointmentTime < dayAfterTomorrowStart
        )
      );

      setShipments1day((prev) => [...prev, ...today]);
      setShipments2day((prev) => [...prev, ...tomorrow]);

      setIsLoading(false);
    };

    // Initial fetch
    getAnalyticsData();

    // Set up interval for subsequent fetches (every 10 minutes)
    const interval = setInterval(() => {
      getAnalyticsData();
    }, 10 * 60 * 1000); // 10 minutes in milliseconds

    // Cleanup function
    return () => {
      clearInterval(interval);
    };
  }, [warehouseId, timeZone, open]);

  return {
    shipmentData,
    shipments1day,
    shipments2day,
    isLoading,
  };
};
